import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import ImageIcon from '@mui/icons-material/Image';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// Matches the gradient options already used by the hardcoded SidePromo
// instances in Home.jsx, so admin-created cards look consistent with them.
const bgOptions = [
    { label: "Orange", value: "bg-gradient-to-b from-primary-orange to-orange-600" },
    { label: "Green", value: "bg-gradient-to-b from-primary-green to-emerald-700" },
    { label: "Blue", value: "bg-gradient-to-b from-primary-blue to-orange-700" },
];

// Flattens the nested category tree into one list, indenting names by
// depth so the hierarchy is still visible in a plain dropdown.
const flattenCategories = (nodes, depth = 0) => {
    let list = [];
    nodes.forEach((node) => {
        list.push({ _id: node._id, label: `${'—'.repeat(depth)} ${node.name}`.trim() });
        if (node.children?.length) {
            list = list.concat(flattenCategories(node.children, depth + 1));
        }
    });
    return list;
};

const PromoCardForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [adminLabel, setAdminLabel] = useState("");
    const [eyebrow, setEyebrow] = useState("");
    const [eyebrowZh, setEyebrowZh] = useState("");
    const [title, setTitle] = useState("");
    const [titleZh, setTitleZh] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [subtitleZh, setSubtitleZh] = useState("");
    const [price, setPrice] = useState("");
    const [bg, setBg] = useState(bgOptions[0].value);
    const [order, setOrder] = useState(0);
    const [active, setActive] = useState(true);
    const [showOverlay, setShowOverlay] = useState(true);
    const [showButton, setShowButton] = useState(true);
    const [buttonPosition, setButtonPosition] = useState("bottom-left");
    const [showOnWebsite, setShowOnWebsite] = useState(true);
    const [showOnApp, setShowOnApp] = useState(true);

    const [image, setImage] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const [imageZh, setImageZh] = useState("");
    const [imageZhPreview, setImageZhPreview] = useState("");
    const [mobileImage, setMobileImage] = useState("");
    const [mobileImagePreview, setMobileImagePreview] = useState("");
    const [mobileImageZh, setMobileImageZh] = useState("");
    const [mobileImageZhPreview, setMobileImageZhPreview] = useState("");

    // Link fields
    const [linkType, setLinkType] = useState("none");
    const [linkProduct, setLinkProduct] = useState(null); // { _id, name } | null
    const [linkCategory, setLinkCategory] = useState(""); // category _id
    const [linkBrand, setLinkBrand] = useState(""); // brand name (string)
    const [linkCustomUrl, setLinkCustomUrl] = useState("");
    const [buttonText, setButtonText] = useState("");
    const [buttonTextZh, setButtonTextZh] = useState("");

    const [productOptions, setProductOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [brandOptions, setBrandOptions] = useState([]);

    // Product/category/brand pickers are needed regardless of edit vs
    // create, since the admin might pick any link type either way.
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [productsRes, categoriesRes, brandsRes] = await Promise.all([
                    axios.get('/api/v1/admin/products'),
                    axios.get('/api/v1/categories'),
                    axios.get('/api/v1/admin/brands'),
                ]);
                setProductOptions(productsRes.data.products.map((p) => ({ _id: p._id, name: p.name })));
                setCategoryOptions(flattenCategories(categoriesRes.data.categories));
                setBrandOptions(brandsRes.data.brands.map((b) => b.name));
            } catch (error) {
                console.error('Failed to load link picker options', error);
            }
        };
        fetchOptions();
    }, []);

    // Same approach as BannerSlideForm: no single-card GET endpoint, so we
    // fetch the admin list and find this one by id — fine for a small list.
    useEffect(() => {
        if (!isEditMode) return;

        const fetchCard = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/promo-cards');
                const card = data.cards.find((c) => c._id === id);
                if (!card) {
                    enqueueSnackbar("Promo card not found", { variant: "error" });
                    navigate('/admin/promo-cards');
                    return;
                }
                setAdminLabel(card.adminLabel || "");
                setEyebrow(card.eyebrow || "");
                setEyebrowZh(card.eyebrowZh || "");
                setTitle(card.title || "");
                setTitleZh(card.titleZh || "");
                setSubtitle(card.subtitle || "");
                setSubtitleZh(card.subtitleZh || "");
                setPrice(card.price || "");
                setBg(card.bg || bgOptions[0].value);
                setOrder(card.order || 0);
                setActive(card.active);
                setShowOverlay(card.showOverlay !== false);
                setShowButton(card.showButton !== false);
                setButtonPosition(card.buttonPosition || "bottom-left");
                setShowOnWebsite(card.showOnWebsite !== false);
                setShowOnApp(card.showOnApp !== false);
                setImagePreview(card.image?.url || "");
                setImageZhPreview(card.imageZh?.url || "");
                setMobileImagePreview(card.mobileImage?.url || "");
                setMobileImageZhPreview(card.mobileImageZh?.url || "");
                setLinkType(card.linkType || "none");
                setLinkProduct(card.linkProduct ? { _id: card.linkProduct._id, name: card.linkProduct.name } : null);
                setLinkCategory(card.linkCategory?._id || "");
                setLinkBrand(card.linkBrand || "");
                setLinkCustomUrl(card.linkCustomUrl || "");
                setButtonText(card.buttonText || "");
                setButtonTextZh(card.buttonTextZh || "");
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load card", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchCard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setImagePreview(reader.result);
                setImage(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleImageZhChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setImageZhPreview(reader.result);
                setImageZh(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleMobileImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setMobileImagePreview(reader.result);
                setMobileImage(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleMobileImageZhChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setMobileImageZhPreview(reader.result);
                setMobileImageZh(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!adminLabel.trim()) {
            enqueueSnackbar("Please enter an admin label (for identifying this card in the list)", { variant: "warning" });
            return;
        }
        if (linkType === 'product' && !linkProduct) {
            enqueueSnackbar("Please choose a product to link to", { variant: "warning" });
            return;
        }
        if (linkType === 'category' && !linkCategory) {
            enqueueSnackbar("Please choose a category to link to", { variant: "warning" });
            return;
        }
        if (linkType === 'brand' && !linkBrand) {
            enqueueSnackbar("Please choose a brand to link to", { variant: "warning" });
            return;
        }
        if (linkType === 'custom' && !linkCustomUrl.trim()) {
            enqueueSnackbar("Please enter a URL", { variant: "warning" });
            return;
        }

        const payload = {
            adminLabel, eyebrow, eyebrowZh, title, titleZh, subtitle, subtitleZh, price, bg, order, active,
            showOverlay, showButton, buttonPosition,
            showOnWebsite, showOnApp,
            linkType,
            linkProduct: linkProduct?._id,
            linkCategory,
            linkBrand,
            linkCustomUrl,
            buttonText,
            buttonTextZh,
        };
        if (image) payload.image = image;
        if (imageZh) payload.imageZh = imageZh;
        if (mobileImage) payload.mobileImage = mobileImage;
        if (mobileImageZh) payload.mobileImageZh = mobileImageZh;

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/promo-card/${id}`, payload);
                enqueueSnackbar("Promo card updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/promo-card/new', payload);
                enqueueSnackbar("Promo card created", { variant: "success" });
            }
            navigate('/admin/promo-cards');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save card", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Promo Card | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="promoform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Promo Card</h1>

                <TextField
                    label="Admin Label"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    value={adminLabel}
                    onChange={(e) => setAdminLabel(e.target.value)}
                    helperText="For identifying this card in the admin list only — never shown on the website or app"
                />

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Eyebrow (small label above title)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={eyebrow}
                        onChange={(e) => setEyebrow(e.target.value)}
                    />
                    <TextField
                        label="Eyebrow (中文 — optional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={eyebrowZh}
                        onChange={(e) => setEyebrowZh(e.target.value)}
                    />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Title (optional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        helperText="Shown as the card's heading — leave blank for an image-only card"
                    />
                    <TextField
                        label="Title (中文 — optional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={titleZh}
                        onChange={(e) => setTitleZh(e.target.value)}
                    />
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Subtitle</p>
                    <ReactQuill theme="snow" value={subtitle} onChange={setSubtitle} />
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Subtitle (中文 — optional)</p>
                    <ReactQuill theme="snow" value={subtitleZh} onChange={setSubtitleZh} />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Price (optional, e.g. HK$49)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                    />
                    <TextField
                        label="Background Color"
                        select
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={bg}
                        onChange={(e) => setBg(e.target.value)}
                    >
                        {bgOptions.map((opt) => (
                            <MenuItem value={opt.value} key={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Display Order"
                        type="number"
                        variant="outlined"
                        size="small"
                        value={order}
                        onChange={(e) => setOrder(e.target.value)}
                        helperText="Lower numbers show first"
                    />
                </div>

                <FormControlLabel
                    control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                    label="Active (visible on homepage)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOverlay} onChange={(e) => setShowOverlay(e.target.checked)} />}
                    label="Dark overlay (improves text readability over busy photos)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showButton} onChange={(e) => setShowButton(e.target.checked)} />}
                    label="Show button (only takes effect if a link and button text are set below)"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOnWebsite} onChange={(e) => setShowOnWebsite(e.target.checked)} />}
                    label="Show on Website"
                />

                <FormControlLabel
                    control={<Checkbox checked={showOnApp} onChange={(e) => setShowOnApp(e.target.checked)} />}
                    label="Show on Customer App"
                />

                {showButton && (
                    <TextField
                        label="Button Position"
                        select
                        variant="outlined"
                        size="small"
                        value={buttonPosition}
                        onChange={(e) => setButtonPosition(e.target.value)}
                        sx={{ maxWidth: 260 }}
                    >
                        <MenuItem value="bottom-left">Bottom Left</MenuItem>
                        <MenuItem value="bottom-center">Bottom Center</MenuItem>
                        <MenuItem value="bottom-right">Bottom Right</MenuItem>
                        <MenuItem value="center-left">Center Left</MenuItem>
                        <MenuItem value="center-center">Center</MenuItem>
                        <MenuItem value="center-right">Center Right</MenuItem>
                        <MenuItem value="top-left">Top Left</MenuItem>
                        <MenuItem value="top-center">Top Center</MenuItem>
                        <MenuItem value="top-right">Top Right</MenuItem>
                    </TextField>
                )}

                {/* --- Link settings --- */}
                <div className="border rounded-lg p-4 flex flex-col gap-4 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">
                        Card Link — on the website this shows a real button; on the mobile app the whole card becomes tappable instead.
                    </p>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        Note: "Custom URL" cards open an external browser, so they're hidden from the mobile app
                        entirely (not shown at all there) — only Product, Category, and Brand links appear on the app.
                    </p>

                    <TextField
                        label="Link Type"
                        select
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={linkType}
                        onChange={(e) => setLinkType(e.target.value)}
                    >
                        <MenuItem value="none">No link</MenuItem>
                        <MenuItem value="product">A specific product</MenuItem>
                        <MenuItem value="category">A category (product listing)</MenuItem>
                        <MenuItem value="brand">A brand</MenuItem>
                        <MenuItem value="custom">Custom URL (website only)</MenuItem>
                    </TextField>

                    {linkType === 'product' && (
                        <Autocomplete
                            options={productOptions}
                            getOptionLabel={(opt) => opt.name || ''}
                            isOptionEqualToValue={(opt, val) => opt._id === val._id}
                            value={linkProduct}
                            onChange={(e, newValue) => setLinkProduct(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Choose a product" size="small" placeholder="Search products..." />
                            )}
                        />
                    )}

                    {linkType === 'category' && (
                        <TextField
                            label="Choose a category"
                            select
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={linkCategory}
                            onChange={(e) => setLinkCategory(e.target.value)}
                        >
                            {categoryOptions.map((opt) => (
                                <MenuItem value={opt._id} key={opt._id}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    {linkType === 'brand' && (
                        <Autocomplete
                            options={brandOptions}
                            value={linkBrand || null}
                            onChange={(e, newValue) => setLinkBrand(newValue || "")}
                            renderInput={(params) => (
                                <TextField {...params} label="Choose a brand" size="small" placeholder="Search brands..." />
                            )}
                        />
                    )}

                    {linkType === 'custom' && (
                        <TextField
                            label="URL (e.g. https://example.com/page)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={linkCustomUrl}
                            onChange={(e) => setLinkCustomUrl(e.target.value)}
                        />
                    )}

                    {linkType !== 'none' && (
                        <div className="flex flex-col sm:flex-row gap-4">
                            <TextField
                                label={`Button Text (website only, e.g. "Shop Now")`}
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={buttonText}
                                onChange={(e) => setButtonText(e.target.value)}
                            />
                            <TextField
                                label="Button Text (中文 — optional)"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={buttonTextZh}
                                onChange={(e) => setButtonTextZh(e.target.value)}
                            />
                        </div>
                    )}
                </div>
                {/* --- Link settings --- */}

                <div>
                    <p className="text-sm text-gray-600 mb-1">Image — used on the website</p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!imagePreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={imagePreview} alt="Promo" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Image (中文 — optional)</p>
                    <p className="text-xs text-gray-400 mb-2">Shown instead of the image above when a visitor switches to Chinese. Leave blank to reuse the image above.</p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!imageZhPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={imageZhPreview} alt="Promo (Chinese)" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageZhChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Mobile App Image (optional)</p>
                    <p className="text-xs text-gray-400 mb-2">
                        The mobile app shows this card in a much shorter, wider shape than the website — using the
                        same image for both often crops badly. Upload a separate image here if that's happening;
                        recommended size ~450 x 390px. Leave blank to just reuse the website image above.
                    </p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!mobileImagePreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={mobileImagePreview} alt="Mobile Promo" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleMobileImageChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-1">Mobile App Image (中文 — optional)</p>
                    <div className="flex items-center gap-4">
                        <div className="w-40 h-24 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!mobileImageZhPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={mobileImageZhPreview} alt="Mobile Promo (Chinese)" className="w-full h-full object-cover" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleMobileImageZhChange}
                                className="hidden"
                            />
                            Choose Image
                        </label>
                    </div>
                </div>

                <div className="flex justify-end">
                    <input form="promoform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Card"} />
                </div>

            </form>
        </>
    );
};

export default PromoCardForm;